import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ArrowUpRight, BookOpen, Users, Sprout, GraduationCap } from "lucide-react";
import { api } from "../lib/api";

export default function Home() {
  const { profile } = useOutletContext() || {};
  const [activities, setActivities] = useState([]);
  const [pubs, setPubs] = useState([]);

  useEffect(() => {
    api.get("/activities").then((r) => setActivities(r.data.slice(0, 3))).catch(() => {});
    api.get("/publications").then((r) => setPubs(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const p = profile || {};

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden mandala-bg">
        <div className="absolute inset-0 pointer-events-none opacity-40 block-print-pattern" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="text-xs tracking-[0.35em] uppercase text-[#C24D2C]">
              Research · Extension · Impact
            </p>
            <h1 className="mt-6 font-serif-display font-bold text-[#1A2456] text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              {p.name || "Dr. Ruchi Singh"}
              <span className="block text-[#E35D29] italic font-medium text-3xl sm:text-4xl lg:text-5xl mt-3">
                weaving academia with the fabric of communities.
              </span>
            </h1>
            <p className="mt-8 text-lg text-[#5C544F] leading-relaxed max-w-2xl">
              {p.bio ||
                "Assistant Professor engaged in academic research and extension activities across rural Madhya Pradesh — bridging classroom scholarship and grassroots impact."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/research"
                data-testid="hero-cta-research"
                className="group inline-flex items-center gap-2 bg-[#E35D29] text-white px-7 py-4 hover:bg-[#C84B1D] transition-colors"
              >
                Explore Research
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/activities"
                data-testid="hero-cta-activities"
                className="inline-flex items-center gap-2 border border-[#C24D2C] text-[#C24D2C] px-7 py-4 hover:bg-[#C24D2C] hover:text-white transition-colors"
              >
                See Extension Work
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative fade-up delay-200">
            <div className="relative">
              <div className="absolute -inset-6 bg-[#E35D29]/15 translate-x-6 translate-y-6" />
              <img
                data-testid="hero-image"
                src={p.photo_url || "https://images.unsplash.com/photo-1590650153855-d9e808231d41?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"}
                alt={p.name || "Dr. Ruchi Singh"}
                className="relative w-full aspect-[4/5] object-cover object-right"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#1A2456] text-white px-6 py-4">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#E35D29]">Based in</p>
                <p className="font-serif-display text-xl">{p.institution || "Madhya Pradesh, India"}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="chevron-divider" />
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
        <div className="grid md:grid-cols-4 gap-8">
          {[
            { icon: GraduationCap, title: "Academic Teaching", desc: "Undergraduate & postgraduate mentorship." },
            { icon: BookOpen, title: "Applied Research", desc: "Community-focused, evidence-based inquiry." },
            { icon: Users, title: "Extension Work", desc: "On-ground outreach across rural districts." },
            { icon: Sprout, title: "Capacity Building", desc: "Training for grassroots empowerment." },
          ].map((f, i) => (
            <div key={f.title} data-testid={`pillar-${i}`} className="group">
              <div className="h-14 w-14 flex items-center justify-center bg-[#F5EFE6] group-hover:bg-[#E35D29] group-hover:text-white transition-colors">
                <f.icon size={22} className="text-[#C24D2C] group-hover:text-white" />
              </div>
              <h3 className="mt-5 font-serif-display text-xl text-[#1A2456]">{f.title}</h3>
              <p className="mt-2 text-sm text-[#5C544F] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED ACTIVITIES */}
      <section className="bg-[#F5EFE6]/60 py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#C24D2C]">On the ground</p>
              <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#1A2456]">
                Recent extension activities
              </h2>
            </div>
            <Link to="/activities" data-testid="view-all-activities" className="link-underline text-sm text-[#E35D29]">
              View all activities →
            </Link>
          </div>

          {activities.length === 0 ? (
            <p className="text-[#8A817C] italic">Activities will appear here once added.</p>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {activities.map((a) => (
                <article key={a.id} data-testid={`home-activity-${a.id}`} className="card-lift bg-white border border-[#D8CFC4]">
                  {a.image_url && (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={a.image_url} alt={a.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6">
                    {a.date && <p className="text-[10px] tracking-[0.25em] uppercase text-[#8A817C]">{a.date}</p>}
                    <h3 className="mt-2 font-serif-display text-xl text-[#1A2456]">{a.title}</h3>
                    <p className="mt-2 text-sm text-[#5C544F] line-clamp-3">{a.description}</p>
                    {a.location && <p className="mt-3 text-xs text-[#C24D2C]">📍 {a.location}</p>}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FEATURED PUBLICATIONS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C24D2C]">Scholarship</p>
            <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#1A2456]">
              Selected publications
            </h2>
            <p className="mt-6 text-[#5C544F] leading-relaxed">
              Peer-reviewed writing that documents fieldwork, policy analysis and community outcomes.
            </p>
            <Link to="/publications" data-testid="view-all-publications" className="mt-6 inline-block link-underline text-sm text-[#E35D29]">
              View full list →
            </Link>
          </div>
          <div className="lg:col-span-8 space-y-6">
            {pubs.length === 0 && <p className="text-[#8A817C] italic">Publications will appear here.</p>}
            {pubs.map((pub, i) => (
              <div key={pub.id} data-testid={`home-pub-${pub.id}`} className="flex gap-6 border-b border-[#D8CFC4] pb-6">
                <span className="font-serif-display text-4xl text-[#E35D29]">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1">
                  <h3 className="font-serif-display text-xl text-[#1A2456]">{pub.title}</h3>
                  <p className="mt-1 text-sm text-[#5C544F]">{pub.authors}</p>
                  <p className="mt-2 text-xs tracking-wider uppercase text-[#8A817C]">
                    {pub.journal} {pub.year && `· ${pub.year}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#1A2456] text-white overflow-hidden">
        <div className="absolute inset-0 block-print-pattern opacity-20 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 grid md:grid-cols-2 gap-10 items-center">
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight">
            Interested in collaboration or a guest lecture?
          </h2>
          <div className="flex justify-start md:justify-end">
            <Link
              to="/contact"
              data-testid="cta-write-to-me"
              className="inline-flex items-center gap-2 bg-[#E35D29] text-white px-8 py-4 hover:bg-[#C84B1D] transition-colors"
            >
              Write to me <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
